import { useCallback, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useStorageState } from 'hooks/useStorageState';
import {
  type LocalCartItem,
  type LocalCartItemPricing,
  type Ticket,
  type TicketAddonItem,
  type TicketsCartQueryData,
  toTotalPrice,
  useTicketsCart,
  useTicketsCreateCart,
  useTicketsReplaceCart,
} from 'restful/tickets';
import {
  capLocalCartAddons,
  hasPaidCatalogueAddons,
  mapLocalCartItemsToCartItemInput,
  upsertLocalCartAddon,
} from 'restful/tickets/addon.utils';
import { RestApiError } from 'services/api/restful/errors';
import { useTrackQuantityChange } from 'services/datalayer/ecomm/useTrackQuantityChange';
import { useTrackViewItem } from 'services/datalayer/ecomm/useTrackViewItem';
import { useCartStore } from 'services/stores/cart';
import { TicketsPushAppField } from 'types/api/dato-extensions.types';

import { useTicketInfoLookup } from 'components/@tickets/hooks';
import { getLocalCartChildRows } from 'components/@tickets/TicketInfoCard/utils';

interface CreateOrUpdateCartVariables {
  items: Array<LocalCartItem>;
  holdToken?: string;
}

interface CreateOrReplaceCartOptions {
  onSuccess?: (data: TicketsCartQueryData) => void;
  onError?: (error: RestApiError) => void;
}

export const useCreateOrReplaceCart = () => {
  const { createCart, isLoading: createCartLoading } = useTicketsCreateCart();
  const { replaceCart, isLoading: replaceCartLoading } =
    useTicketsReplaceCart();
  const { cart } = useTicketsCart();

  const handleCreateCartOrReplaceCart = useCallback(
    (
      { items, holdToken }: CreateOrUpdateCartVariables,
      options?: CreateOrReplaceCartOptions,
    ) => {
      const mappedItems = mapLocalCartItemsToCartItemInput(items);
      // Only replace the cart if we have matching hold tokens
      if (!!cart && cart.seatedHoldToken?.token === holdToken) {
        return replaceCart({ items: mappedItems }, options);
      } else {
        return createCart({ items: mappedItems, holdToken }, options);
      }
    },
    [cart, createCart, replaceCart],
  );

  return {
    createOrReplaceCart: handleCreateCartOrReplaceCart,
    isLoading: createCartLoading || replaceCartLoading,
  };
};

export const useAppPushState = (
  eventKey: string,
  ticketsPushApp: TicketsPushAppField,
  hasTicketOffers: boolean,
) => {
  const [appPushSoftIgnoredString, setAppPushSoftIgnored] = useStorageState(
    `nf-app-push-soft-ignored-${eventKey}`,
    {
      defaultValue: 'false',
      engine: 'sessionStorage',
    },
  );
  const showAppPushDialog =
    ticketsPushApp === 'hard' ||
    (ticketsPushApp === 'soft' && appPushSoftIgnoredString !== 'true');

  return {
    showAppPushDialog: hasTicketOffers ? false : showAppPushDialog,
    setAppPushSoftIgnored,
  };
};

interface UseTicketDetailSheetParams {
  ticketsById: Map<string, Ticket>;
  /** Returns false when blocked by auth, push-app gate, or cart-max limit. */
  onAddTicket: (ticket: Ticket) => boolean;
}

export const useTicketDetailSheet = ({
  ticketsById,
  onAddTicket,
}: UseTicketDetailSheetParams) => {
  const [cartItems] = useCartStore(useShallow((state) => [state.items]));
  const { setItemAddons } = useCartStore((state) => state.actions);
  const lookup = useTicketInfoLookup();
  const handleViewItem = useTrackViewItem();
  const { trackPaidAddonDiff } = useTrackQuantityChange();

  const [detailTicket, setDetailTicket] = useState<Ticket | undefined>();

  const detailCartItem = cartItems.find(
    (item) => item.catalogueItemId === detailTicket?.id,
  );

  const isSeatedDetailTicket = !!(
    detailTicket?.seatingData?.seatingChartEventOrSeasonId ||
    detailTicket?.seatingData?.seatingCategoryKey
  );

  const showAddonSelection =
    !!detailTicket && hasPaidCatalogueAddons(detailTicket);

  const canSelectAddons = !!detailTicket && !detailTicket.isExternalForm;

  const showParentQuantity =
    !!detailTicket && !detailTicket.isExternalForm && !isSeatedDetailTicket;

  const handleOpenTicketDetail = useCallback(
    (ticket: Ticket) => {
      handleViewItem(ticket);
      setDetailTicket(ticket);
    },
    [handleViewItem],
  );

  const handleCloseDetail = useCallback(() => setDetailTicket(undefined), []);

  const handleAddTicketOrOpenAddons = (ticket: Ticket) => {
    if (hasPaidCatalogueAddons(ticket)) {
      if (!onAddTicket(ticket)) {
        return;
      }

      handleOpenTicketDetail(ticket);
      return;
    }

    onAddTicket(ticket);
  };

  const handleChangeAddon = useCallback(
    (addon: TicketAddonItem, quantity: number) => {
      if (!detailTicket) {
        return;
      }

      const current = cartItems.find(
        (item) => item.catalogueItemId === detailTicket.id,
      );

      if (!current) {
        return;
      }

      const nextAddons = capLocalCartAddons(
        upsertLocalCartAddon(current.addons, {
          catalogueItemId: addon.id,
          name: addon.name ?? current.name,
          quantity,
          ticketType: addon.ticketType,
          addonType: addon.addonType,
          maxQuantityPerParentItem: addon.maxQuantityPerParentItem,
        }) ?? [],
        current.quantity,
      );

      trackPaidAddonDiff(current.addons, nextAddons);
      setItemAddons(detailTicket.id, nextAddons ?? []);
    },
    [cartItems, detailTicket, setItemAddons, trackPaidAddonDiff],
  );

  /** After seating confirmation, open the addon detail sheet for the first seated ticket that has paid addons. */
  const handleSeatsConfirmed = useCallback(
    (ticketIds: Array<string>) => {
      const ticket = ticketIds
        .map((ticketId) => ticketsById.get(ticketId))
        .find(
          (nextTicket) =>
            nextTicket &&
            hasPaidCatalogueAddons(nextTicket) &&
            cartItems.some(
              (item) =>
                item.catalogueItemId === nextTicket.id && item.quantity > 0,
            ),
        );

      if (ticket) {
        handleOpenTicketDetail(ticket);
      }
    },
    [cartItems, ticketsById, handleOpenTicketDetail],
  );

  const getOrderSummaryChildRows = useCallback(
    (item: LocalCartItemPricing) =>
      getLocalCartChildRows(
        item,
        ticketsById.get(item.catalogueItemId),
        (catalogueItemId, quantity) =>
          toTotalPrice(lookup(catalogueItemId).discountedPrice, quantity),
      ),
    [ticketsById, lookup],
  );

  return {
    detailTicket,
    detailCartItem,
    isSeatedDetailTicket,
    showAddonSelection,
    canSelectAddons,
    showParentQuantity,
    handleOpenTicketDetail,
    handleCloseDetail,
    handleAddTicketOrOpenAddons,
    handleChangeAddon,
    handleSeatsConfirmed,
    getOrderSummaryChildRows,
  };
};
